import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Panel,
  Flex,
  Label,
  TextInput,
  TextArea,
  Select,
  FormHeading,
  Divider,
  Button,
  Toggle
} from '@sopranodesign/pulse-components';
import { useQuery } from '@sopranodesign/pulse-hooks';

const DEFAULT_FORM = {
  mcp_skill_provider_id: null,
  name: '',
  description: '',
  enabled: true,
  input_schema: JSON.stringify(
    {
      type: 'object',
      properties: {},
      required: []
    },
    null,
    2
  ),
  output_schema: ''
};

const McpSkillDetails = ({ handleSubmit, loading, initialSkill }) => {
  const [form, setForm] = useState(() => ({
    ...DEFAULT_FORM,
    ...initialSkill,
    input_schema:
      initialSkill?.input_schema
        ? JSON.stringify(initialSkill.input_schema, null, 2)
        : DEFAULT_FORM.input_schema,
    output_schema:
      initialSkill?.output_schema
        ? JSON.stringify(initialSkill.output_schema, null, 2)
        : DEFAULT_FORM.output_schema
  }));

  const [schemaError, setSchemaError] = useState(null);

  const providersQuery = useQuery({ queryKey: ['mcp-skill-providers'] });

  const providerOptions = (providersQuery.data?.data ?? []).map(p => ({
    value: p.id,
    label: p.name
  }));

  const selectedProvider = providerOptions.find(
    p => p.value === form.mcp_skill_provider_id
  );

  const valid = Boolean(
    form.mcp_skill_provider_id &&
      form.name &&
      form.description &&
      !schemaError
  );

  const setField = (key, value) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleInputSchemaChange = e => {
    const value = e.target.value;
    setField('input_schema', value);
    try {
      JSON.parse(value);
      setSchemaError(null);
    } catch {
      setSchemaError('Invalid JSON');
    }
  };

  const handleOutputSchemaChange = e => {
    const value = e.target.value;
    setField('output_schema', value);
    if (value) {
      try {
        JSON.parse(value);
        setSchemaError(null);
      } catch {
        setSchemaError('Invalid JSON in output schema');
      }
    }
  };

  const onSubmit = event => {
    event.preventDefault();
    if (!valid || loading) {return};

    handleSubmit({
      mcp_skill_provider_id: form.mcp_skill_provider_id,
      name: form.name,
      description: form.description,
      enabled: form.enabled,
      input_schema: JSON.parse(form.input_schema),
      output_schema: form.output_schema ? JSON.parse(form.output_schema) : null
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <Panel>
        <Flex col>
          <FormHeading>General</FormHeading>
          <Flex xSpace>
            <Flex col fat mb>
              <Label htmlFor="skill_provider">Provider</Label>
              <Select
                id="skill_provider"
                placeholder="Select a provider..."
                options={providerOptions}
                isLoading={providersQuery.isLoading}
                value={selectedProvider ?? null}
                onChange={opt => setField('mcp_skill_provider_id', opt.value)}
              />
            </Flex>
            <Flex col fat mb center>
              <Label>Enabled</Label>
              <Toggle
                on={form.enabled}
                handleToggleChange={() => setField('enabled', !form.enabled)}
                title="skill-enabled"
              />
            </Flex>
          </Flex>

          <Flex xSpace>
            <Flex col fat mb>
              <Label htmlFor="skill_name">Name</Label>
              <TextInput
                id="skill_name"
                placeholder="write_file"
                value={form.name}
                onChange={e => setField('name', e.target.value)}
              />
            </Flex>
          </Flex>

          <Flex col fat mb>
            <Label htmlFor="skill_description">Description</Label>
            <TextInput
              id="skill_description"
              placeholder="What this skill does..."
              value={form.description}
              onChange={e => setField('description', e.target.value)}
            />
          </Flex>

          <Divider fullWidth />
          <FormHeading>Schemas</FormHeading>

          <Flex col fat mb>
            <Label htmlFor="skill_input_schema">
              Input schema (JSON Schema)
            </Label>
            <TextArea
              id="skill_input_schema"
              rows={8}
              value={form.input_schema}
              onChange={handleInputSchemaChange}
            />
          </Flex>

          <Flex col fat mb>
            <Label htmlFor="skill_output_schema">
              Output schema (optional)
            </Label>
            <TextArea
              id="skill_output_schema"
              rows={5}
              placeholder='{ "type": "object", ... }'
              value={form.output_schema}
              onChange={handleOutputSchemaChange}
            />
          </Flex>

          {schemaError && (
            <Flex mb>
              <Label colour="danger">{schemaError}</Label>
            </Flex>
          )}
        </Flex>
      </Panel>

      <Flex right mt>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={!valid || loading}>
          Save
        </Button>
      </Flex>
    </form>
  );
};

McpSkillDetails.defaultProps = {
  initialSkill: null
};

McpSkillDetails.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  initialSkill: PropTypes.object
};

export default McpSkillDetails;
